import {
    PerformanceManagementAlertActivityStatus, PerformanceManagementAlertEmailTitleType, PerformanceManagementAlertScore,
    PerformanceManagementAlertTriggerType, RequestStatus, defaults, entities
} from 'typed/api';
import { Action, UntypedAction } from 'typed/actions';
import {
    childrenReducer, combineReducers, entityAddAllReducer, entityAddReducer, entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer,
    entityEditReducer, entityLoadAllReducer, entityLoadReducer, entityVoidAllReducer, networkActionReducer, sortedEntitiesReducer, switchReducers,
    synchronizableEnumReducer
} from './utils';
import { deepAssign } from 'typed/utils';


// TODO improve how `switchReducers` and `combineReducers` work; both of them have `defaultState` parameter, which is applied before any call
// so their children reducers will never face an empty state and therefore don't need `defaultState`
// so there should be special type of reducer without empty state option, and special versions of entity*Reducer without `defaultState` generating those special reducers
// but that's a lot of work...
// for now let's reduce amount of visual noise and accesses to `defaults` (which actually might be slow since every attempt to access creates a new copy of an entity)
// with these very short aliases
const
    a = defaults.PerformanceManagementAlertReport,
    as = defaults.NetworkArrayOf(a),
    ab = defaults.NestedNetworkPaginationSliceOf(a),
    abs: entities.NestedNetworkPaginationSlice<entities.PerformanceManagementAlertReport>[] = [],
    p = a.Custom.preview,
    e = a.Custom.evaluation,
    rs = [] as entities.PerformanceManagementAlertRecipient[],
    ts = [] as entities.PerformanceManagementAlertTriggerIndicator[],
    is = [] as entities.PerformanceManagementAlertIndicator[],
    hs = [] as entities.PerformanceManagementAlertReviewSectionReport[],
    bs = [] as entities.PerformanceManagementAlertDashboardBlockReport[];

export default combineReducers({
    performanceManagementAlertActivityStatuses: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.PerformanceManagementAlertActivityStatus), 'LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERT_ACTIVITY_STATUSES', PerformanceManagementAlertActivityStatus as any, 'PerformanceManagementAlertActivityStatus'),
    performanceManagementAlertTriggerTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.PerformanceManagementAlertTriggerType), 'LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_TYPES', PerformanceManagementAlertTriggerType as any, 'PerformanceManagementAlertTriggerType'),
    performanceManagementAlertScores: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.PerformanceManagementAlertScore), 'LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERT_SCORES', PerformanceManagementAlertScore as any, 'PerformanceManagementAlertScore'),
    performanceManagementAlertEmailTitleTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.PerformanceManagementAlertEmailTitleType), 'LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERT_EMAIL_TITLE_TYPES', PerformanceManagementAlertEmailTitleType as any, 'PerformanceManagementAlertEmailTitleType'),
    performanceManagementAlerts: sortedEntitiesReducer(as, 'Name', switchReducers(as,
        entityLoadAllReducer(as, 'LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERTS'),
        entityAddReducer(as, 'ADD_PERFORMANCE_MANAGEMENT_ALERT'),
        entityEditReducer(as, 'EDIT_PERFORMANCE_MANAGEMENT_ALERT'),
        childrenReducer(as,
            switchReducers(a,
                combineReducers(a, {
                    Recipients: switchReducers(rs,
                        entityAddAllReducer(rs, 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_RECIPIENTS'),
                        entityDeleteAllReducer(rs, 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_RECIPIENTS')
                    ),
                    TriggerIndicators: switchReducers(ts,
                        entityAddAllReducer(ts, 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS'),
                        entityEditAllReducer(ts, 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS'),
                        entityDeleteAllReducer(ts, 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS')
                    ),
                    Indicators: switchReducers(is,
                        entityAddAllReducer(is, 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS'),
                        entityEditAllReducer(is, 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS'),
                        entityDeleteAllReducer(is, 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS')
                    ),
                    Sections: switchReducers(hs,
                        entityAddReducer(hs, 'ADD_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'),
                        entityEditReducer(hs, 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'),
                        entityDeleteReducer(hs, 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'),
                        (state, action) => {
                            let newState = state || hs;

                            if (action.type === 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION_COMMENTS' ||
                               action.type === 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION_COMMENTS') {
                                const reducer = action.type === 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION_COMMENTS' ?
                                    entityAddAllReducer :
                                    entityEditAllReducer;

                                (action.payload.entities as entities.PerformanceManagementAlertReviewSectionComment[])
                                    .groupBy(({ ReviewSectionID }) => action.payload.parentID || ReviewSectionID)
                                    .forEach((group) => {
                                        const parent = newState.getByID(group.key);

                                        if (parent) {
                                            const parentAction = deepAssign(action, { payload: { response: [...group] } });

                                            newState = deepAssign(newState, {
                                                [newState.getIndexByID(group.key)]: {
                                                    Comments: reducer(parent.Comments, action.type)(parent.Comments, parentAction)
                                                }
                                            });
                                        }
                                    });
                            }

                            return newState;
                        }
                    ),
                    Blocks: switchReducers(bs,
                        entityAddAllReducer(bs, 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'),
                        entityAddReducer(bs, 'ADD_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'),
                        entityEditAllReducer(bs, 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'),
                        entityEditReducer(bs, 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'),
                        entityDeleteAllReducer(bs, 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'),
                        entityDeleteReducer(bs, 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'),
                        (state, action) => {
                            let newState = state || bs;

                            if (action.type === 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK_COMMENTS' ||
                               action.type === 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK_COMMENTS') {
                                const reducer = action.type === 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK_COMMENTS' ?
                                    entityAddAllReducer :
                                    entityEditAllReducer;

                                (action.payload.entities as entities.PerformanceManagementAlertDashboardBlockComment[])
                                    .groupBy(({ ReviewSectionID }) => action.payload.parentID || ReviewSectionID)
                                    .forEach((group) => {
                                        const parent = newState.getByID(group.key);

                                        if (parent) {
                                            const parentAction = deepAssign(action, { payload: { response: [...group] } });

                                            newState = deepAssign(newState, {
                                                [newState.getIndexByID(group.key)]: {
                                                    Comments: reducer(parent.Comments, action.type)(parent.Comments, parentAction)
                                                }
                                            });
                                        }
                                    });
                            }

                            return newState;
                        }
                    ),
                    Custom: combineReducers(a.Custom, {
                        preview: (state = p, action) =>
                            action.type === 'SEND_PERFORMANCE_MANAGEMENT_ALERT_PREVIEW' ||
                            action.type === 'SEND_PERFORMANCE_MANAGEMENT_ALERT_REVIEW' ||
                            action.type === 'SEND_PERFORMANCE_MANAGEMENT_ALERT_REPORT' ?
                                deepAssign(state, {
                                    UserID: action.payload.userID,
                                    Custom: {
                                        status: action.payload.status
                                    }
                                }) :
                                state,
                        evaluation: networkActionReducer(e, 'EVALUATE_PERFORMANCE_MANAGEMENT_ALERT')
                    })
                }),
                (state = a, action: Action<
                | 'ADD_PERFORMANCE_MANAGEMENT_ALERT'
                | 'EDIT_PERFORMANCE_MANAGEMENT_ALERT'
                | 'SEND_PERFORMANCE_MANAGEMENT_ALERT_REPORT'
                | 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_RECIPIENTS'
                | 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_RECIPIENTS'
                | 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS'
                | 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS'
                | 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS'
                | 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS'
                | 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS'
                | 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS'
                | 'ADD_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'
                | 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'
                | 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'
                | 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'
                | 'ADD_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'
                | 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'
                | 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'
                | 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'
                | 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'
                >) => {
                    switch (action.type) {
                        case 'ADD_PERFORMANCE_MANAGEMENT_ALERT':
                        case 'EDIT_PERFORMANCE_MANAGEMENT_ALERT':
                        case 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_RECIPIENTS':
                        case 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_RECIPIENTS':
                        case 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS':
                        case 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS':
                        case 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_TRIGGER_INDICATORS':
                        case 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS':
                        case 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS':
                        case 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_INDICATORS':
                        case 'ADD_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION':
                        case 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION':
                        case 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION':
                        case 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS':
                        case 'ADD_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK':
                        case 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS':
                        case 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK':
                        case 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS':
                        case 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK':
                            return action.payload.status === RequestStatus.SUCCEEDED ?
                                deepAssign(state!, { Updated: new Date() }) :
                                state!;

                        case 'SEND_PERFORMANCE_MANAGEMENT_ALERT_REPORT':
                            return action.payload.status === RequestStatus.SUCCEEDED && action.payload.response && !!action.payload.response.SentToRecipients ?
                                deepAssign(state!, { LastSent: action.payload.response.SentToRecipients }) :
                                state!;

                        default:
                            return state!;
                    }
                },

                (state = a, action: Action<
                | 'ADD_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'
                | 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'
                | 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION'
                | 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'
                | 'ADD_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'
                | 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'
                | 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'
                | 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS'
                | 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK'
                >) => {
                    switch (action.type) {
                        case 'ADD_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION':
                        case 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION':
                        case 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_REVIEW_SECTION':
                            const
                                actionReviewSection = 'entity' in action.payload && action.payload.entity.Sort ? action.payload.entity : null,
                                pinnedReviewSection = actionReviewSection ?
                                    state.Sections.getByID(actionReviewSection.ID) ||
                                        state.Sections.find(({ Custom: { internalID } }) => internalID === actionReviewSection!.ID) :
                                    null;

                            let otherSectionsAndBlocks = [
                                ...state.Sections.filter(reviewSection => pinnedReviewSection !== reviewSection),
                                ...state.Blocks
                            ];

                            otherSectionsAndBlocks.sortBy(({ Sort }) => Sort || Infinity);

                            if (pinnedReviewSection) {
                                otherSectionsAndBlocks.splice(pinnedReviewSection.Sort - 1, 0, pinnedReviewSection);
                            }

                            otherSectionsAndBlocks = otherSectionsAndBlocks.map((item, index) => ({ ...item, Sort: index + 1 }));

                            return deepAssign(state!, {
                                Sections: otherSectionsAndBlocks.filter(({ EntityType }) => EntityType === defaults.PerformanceManagementAlertReviewSectionReport.EntityType) as entities.PerformanceManagementAlertReviewSectionReport[],
                                Blocks: otherSectionsAndBlocks.filter(({ EntityType }) => EntityType === defaults.PerformanceManagementAlertDashboardBlockReport.EntityType) as entities.PerformanceManagementAlertDashboardBlockReport[]
                            });

                        case 'ADD_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS':
                        case 'EDIT_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS':
                        case 'DELETE_ALL_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCKS':
                            const pinnedBlocks = 'entities' in action.payload ?
                                action.payload.entities
                                    .filter(({ Sort }) => Sort)
                                    .map(block =>
                                        state.Blocks.getByID(block.ID) ||
                                                state.Blocks.find(({ Custom: { internalID } }) => internalID === block.ID)!
                                    ) :
                                [];
                            let nonPinnedBlocksAndSections = [
                                ...state.Sections,
                                ...state.Blocks.filter(column => !pinnedBlocks.includes(column))
                            ];

                            nonPinnedBlocksAndSections.sortBy(({ Sort }) => Sort || Infinity);

                            pinnedBlocks.forEach(item => nonPinnedBlocksAndSections.splice(item.Sort - 1, 0, item));

                            nonPinnedBlocksAndSections = nonPinnedBlocksAndSections.map((column, index) => ({ ...column, Sort: index + 1 }));

                            return deepAssign(state!, {
                                Sections: nonPinnedBlocksAndSections.filter(({ EntityType }) => EntityType === defaults.PerformanceManagementAlertReviewSectionReport.EntityType) as entities.PerformanceManagementAlertReviewSectionReport[],
                                Blocks: nonPinnedBlocksAndSections.filter(({ EntityType }) => EntityType === defaults.PerformanceManagementAlertDashboardBlockReport.EntityType) as entities.PerformanceManagementAlertDashboardBlockReport[]
                            });

                        case 'ADD_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK':
                        case 'EDIT_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK':
                        case 'DELETE_PERFORMANCE_MANAGEMENT_ALERT_DASHBOARD_BLOCK':
                            const actionBlock = 'entity' in action.payload && action.payload.entity.Sort ? action.payload.entity : null,
                                pinnedBlock = actionBlock ?
                                    state.Blocks.getByID(actionBlock.ID) ||
                                        state.Blocks.find(({ Custom: { internalID } }) => internalID === actionBlock!.ID) :
                                    null;

                            let otherBlocksAndSections = [
                                ...state.Sections,
                                ...state.Blocks.filter(block => pinnedBlock !== block)
                            ];

                            otherBlocksAndSections.sortBy(({ Sort }) => Sort || Infinity);

                            if (pinnedBlock) {
                                otherBlocksAndSections.splice(pinnedBlock.Sort - 1, 0, pinnedBlock);
                            }

                            otherBlocksAndSections = otherBlocksAndSections.map((item, index) => ({ ...item, Sort: index + 1 }));

                            return deepAssign(state!, {
                                Sections: otherBlocksAndSections.filter(({ EntityType }) => EntityType === defaults.PerformanceManagementAlertReviewSectionReport.EntityType) as entities.PerformanceManagementAlertReviewSectionReport[],
                                Blocks: otherBlocksAndSections.filter(({ EntityType }) => EntityType === defaults.PerformanceManagementAlertDashboardBlockReport.EntityType) as entities.PerformanceManagementAlertDashboardBlockReport[]
                            });

                        default:
                            return state!;
                    }
                }
            ),
            (state, action) => state.Data.getIndexByID(action.payload && ((action.payload as any).grandParentID || (action.payload as any).parentID || +(action.payload as any).response || (action.payload as any).entityID))
        ),
        (state: entities.NetworkArray<entities.PerformanceManagementAlertReport> = as, action) => {
            if (action.type === 'EVALUATE_PERFORMANCE_MANAGEMENT_ALERT' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(state, { Data: state.Data.map(_ => ({ EvaluationVisible: false })) });
            }

            return state;
        },
        (state: entities.NetworkArray<entities.PerformanceManagementAlertReport> = as, action: UntypedAction) => {
            switch (action.type) {
                case 'ADD_ENTITY_CHANGE':
                    const change: entities.EntityChange = action.change as any,
                        userID: number = action.userID as any;
                    if (change.EntityType === 'PerformanceManagementAlert') {
                        const alert = state.Data.getByID(change.EntityID);

                        if (alert && alert.Custom.preview.Custom.status === RequestStatus.PENDING && alert.Custom.preview.UserID === change.UserID) {
                            return deepAssign(state, {
                                Data: {
                                    [state.Data.getIndexByID(alert.ID)]: {
                                        Custom: {
                                            preview: {
                                                UserID: userID,
                                                Custom: {
                                                    status: RequestStatus.SUCCEEDED
                                                }
                                            }
                                        }
                                    }
                                }
                            });
                        }
                    }

                    return state;

                default:
                    return state;
            }
        },
        entityDeleteReducer(as, 'DELETE_PERFORMANCE_MANAGEMENT_ALERT'),
        entityVoidAllReducer(as, 'VOID_ALL_PERFORMANCE_MANAGEMENT_ALERTS')
    )),
    performanceManagementAlertBatches: switchReducers(abs,
        (state: entities.NestedNetworkPaginationSlice<entities.PerformanceManagementAlertReport>[] | undefined, action) => {
            const newState = (state || abs).copy();

            if (action.type === 'LOAD_PERFORMANCE_MANAGEMENT_ALERT_BATCH_SLICES') {
                const
                    response: entities.NestedNetworkPaginationSlice<entities.PerformanceManagementAlertReport>[] = action.payload.response || [],
                    alertIDs = action.payload.status === RequestStatus.SUCCEEDED ?
                        response.map(({ FatherID }) => FatherID) :
                        action.payload.alertIDs;

                for (let alertIndex = 0; alertIndex < alertIDs.length; alertIndex++) {
                    const
                        alertID = alertIDs[alertIndex],
                        stateIndex = newState.findIndex(({ FatherID }) => FatherID === alertID),
                        oldSlice = newState.find(({ FatherID }) => FatherID === alertID) || deepAssign(ab, { FatherID: alertID }),
                        newSlice = response.find(({ FatherID }) => FatherID === alertID),

                        pendingAction = action.payload.status === RequestStatus.SUCCEEDED ?
                            {
                                ...action,
                                payload: {
                                    ...action.payload,
                                    response: newSlice
                                }
                            } :
                            action;

                    newState[stateIndex < 0 ? newState.length : stateIndex] = entityLoadAllReducer(oldSlice, action.type)(oldSlice, pendingAction);
                }
            }

            if (action.type === 'LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERTS' && action.payload.status === RequestStatus.SUCCEEDED) {
                return (action.payload.response as entities.PerformanceManagementAlertReport[]).leftJoin(
                    newState,
                    ({ ID }) => ID,
                    ({ FatherID }) => FatherID,
                    (alert, batchSlice) => batchSlice || deepAssign(ab, { FatherID: alert.ID })
                );
            }

            if (action.type === 'ADD_PERFORMANCE_MANAGEMENT_ALERT' && action.payload.status === RequestStatus.SUCCEEDED) {
                newState.push(deepAssign(ab, { FatherID: action.payload.response }));
            }

            return newState;
        },
        childrenReducer<any, entities.NestedNetworkPaginationSlice<entities.PerformanceManagementAlertReport>[], any>(abs,
            switchReducers(ab,
                entityLoadReducer(ab, 'REVIEW_AND_SEND_PERFORMANCE_MANAGEMENT_ALERT_BATCH'),
                (state, action) => {
                    let newState = state || ab;

                    if ((action.type === 'SEND_PERFORMANCE_MANAGEMENT_ALERT_REVIEW' || action.type === 'SEND_PERFORMANCE_MANAGEMENT_ALERT_REPORT') &&
                        action.payload && action.payload.status === RequestStatus.SUCCEEDED && action.payload.response) {
                        const data = newState.Data.copy();
                        data.push(action.payload.response);

                        newState = deepAssign(newState, {
                            Count: newState.Count + 1,
                            Data: data,
                            TotalCount: newState.TotalCount + 1
                        });
                    }

                    return newState;
                },
                childrenReducer(ab,
                    combineReducers(a, {
                        Custom: combineReducers(a.Custom, {
                            userLoginURLs: (state, action) => {
                                const newState = state || a.Custom.userLoginURLs;

                                if (action.type === 'CREATE_PERFORMANCE_MANAGEMENT_ALERT_BATCH_USER_LOGIN_URL') {
                                    let urlEntity = newState.getByID(action.payload.userID) || {
                                        ID: action.payload.userID,
                                        Custom: {
                                            status: action.payload.status
                                        },
                                        LoginURL: ''
                                    };

                                    urlEntity = deepAssign(urlEntity, { Custom: { status: action.payload.status } });

                                    if (action.payload.status === RequestStatus.SUCCEEDED) {
                                        urlEntity = deepAssign(urlEntity, { LoginURL: action.payload.response });
                                    }

                                    const urlEntityIndex = newState.getIndexByID(action.payload.userID);
                                    return deepAssign(newState, { [urlEntityIndex >= 0 ? urlEntityIndex : newState.length]: urlEntity });
                                }

                                return newState;
                            }
                        })
                    }),
                    (state, action) => state.Data.getIndexByID((action.payload as unknown as any)?.entityID)
                )
            ),
            (state: entities.NestedNetworkPaginationSlice<entities.PerformanceManagementAlertReport>[], action) =>
                state.findIndex(({ FatherID }) => FatherID === (action.payload as unknown as any)?.parentID)
        )
    )
});
